import { ChangeDetectorRef, Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { XeroCompany } from 'sharedFeatures/sharedModels';
import { XeroLimits } from "sharedFeatures/sharedModels/XeroLimits";
import { DialogCreationService, RedirectService, XeroService } from 'sharedFeatures/sharedServices';
import { TabLinkComponent } from 'sharedFeatures/tab-link/tab-link.component';
import { ButtonComponent } from 'sharedGuiSmall/components/button/button.component';
import { DomSmallUtils } from 'sharedGuiSmall/utils/dom-utils';
import { SharedGuiSmallModule } from '../../../../sharedGuiSmall/sharedGuiSmall.module';
import { ScrollComponent } from '../scroll/scrolldiv.component';
import { ChooseCompanyFormBodyComponent } from './choose-company/choose-company-form-body.component';
import { NewPlanInnerForm } from './newplan-inner.form';
import { NewPlanStartXeroFormBodyComponent } from './newplan-start-xero/newplan-start-xero-form-body.component';
import { NewPlanStartFormBodyComponent } from './newplan-start/newplan-start-form-body.component';
import { NewPlanForm } from './newplan.form';
import { Xi2ProfitLossFormBodyComponent } from './xi-2-profitloss/xi-2-profitloss-form-body.component';
import { Xi3BalanceSheetFormBodyComponent } from './xi-3-balancesheet/xi-3-balancesheet-form-body.component';
import { Xi4DataAssociationsFormBodyComponent } from './xi-4-associations/xi-4-associations-form-body.component';
import { TranslateModule } from '@ngx-translate/core';

class NavItem {
    id: string = '';
    text: string = '';
}

@Component({
    selector: 'newplan-body',
    templateUrl: './newplan-body.component.html',
    styleUrls: ['./newplan-body.component.less'],
    standalone: true,
    imports: [
        SharedGuiSmallModule,
        ScrollComponent,
        NewPlanStartFormBodyComponent,
        ChooseCompanyFormBodyComponent,
        NewPlanStartXeroFormBodyComponent,
        Xi2ProfitLossFormBodyComponent,
        Xi3BalanceSheetFormBodyComponent,
        Xi4DataAssociationsFormBodyComponent,
        TranslateModule
    ],
    providers: [DialogCreationService]
})
export class NewPlanFormBodyComponent {

    @ViewChild(NgForm) private form!: NgForm;

    canContinue: boolean = false;
    parent: NewPlanForm | undefined;

    bottomPaneData: Array<NavItem> = [{ id: 'company', text: 'Choose company' }, { id: 'settings', text: 'Plan settings' }, { id: 'profitloss-accounts', text: 'Profit & Loss accounts' }, { id: 'balancesheet-accounts', text: 'Balance Sheet accounts' }];
    bottomPaneDataRelease: Array<NavItem> = [{ id: 'company', text: 'Choose company' }, { id: 'settings', text: 'Plan settings' }, { id: 'profitloss-accounts', text: 'Profit & Loss accounts' }, { id: 'balancesheet-accounts', text: 'Balance Sheet accounts' }, { id: 'component-associations', text: 'Component associations' }];

    nextTextLocalize = $localize`NEXT`;
    connectPlanText = $localize`CONNECT_PLAN`;
    createPlanText = $localize`CREATE_PLAN`;
    importLimitText = $localize`Daily import limit reached`;
    canNotImportedText = $localize`No more transactions can be imported for this Xero company today. Please try again tomorrow.`;

    activeId: string = 'company';
    nextText: string = this.nextTextLocalize;

    isXeroImportModeEnabled: boolean = false;
    isConnectToExistingModeEnabled: boolean = false;
    
    private activeTabLink: TabLinkComponent | undefined;

    @ViewChild('scroll')
    private scroll!: ScrollComponent;

    @ViewChild('next')
    private nextBtn!: ButtonComponent;

    @ViewChild('startSimple')
    private startFormSimple!: NewPlanStartFormBodyComponent;

    @ViewChild('companyForm')
    private companyForm!: ChooseCompanyFormBodyComponent;

    @ViewChild('startForm')
    private startXeroForm!: NewPlanStartXeroFormBodyComponent;

    @ViewChild('xi2Form')
    private xi2Form!: Xi2ProfitLossFormBodyComponent;

    @ViewChild('balanceForm')
    private balanceForm!: Xi3BalanceSheetFormBodyComponent;
     
    @ViewChild('xi4Form')
    private xi4Form!: Xi4DataAssociationsFormBodyComponent;

    private activeForm: any;

    @ViewChild('saveClosePanePlace')
    private saveClosePanePlace!: ElementRef;

    isConnectToXero: boolean = false;

    get disableScroll() {
        return this.parent?.renderForSignup || this.parent?.disableScroll;
    }

    constructor(private xeroService: XeroService,
                private dialogCreation: DialogCreationService,
                private redirectService: RedirectService,
                private chDet: ChangeDetectorRef,
                private el: ElementRef,
                private r: Renderer2
    ) {}

    detectChanges(): void {
        this.chDet.detectChanges();
    }

    ngOnInit(): void {
        this.activeId = 'company';
    }

    ngAfterViewInit() {
        this.disableItems();
    }

    private disableItems(): void {

        this.el.nativeElement.querySelectorAll(':scope .bottom-div .itemCls').forEach(
            (el: HTMLDivElement) => {
                if (!DomSmallUtils.hasClass(el, 'active')) {
                    this.r.addClass(el, 'disabled')
                }
            }
        )

    }

    public getForm(): NgForm {
        return this.form;
    }

    setParent(form: NewPlanForm): void {
        this.parent = form;
    }

    activateSimplePlanCreate(): void {
        this.activeForm = this.startFormSimple;
    }

    activateCompany(): void {
        this.onActivate({ id: 'company', text: '' });
    }

    activateSettings(): void {
        this.onActivate({ id: 'settings', text: ''});
    }

    activateProfitLoss(): void {
        this.onActivate({ id: 'profitloss-accounts', text: '' });
    }

    activateBalanceSheet(): void {
        this.onActivate({ id: 'balancesheet-accounts', text: '' });
    }

    activateDataImport(): void {
        this.onActivate({ id: 'component-associations', text: '' });
    }

    backFn(): void {
        if (this.parent)
            this.parent.close();
    }

    clearActive(): void {
        if (this.activeTabLink) this.activeTabLink.setClicked(false);
        this.activeTabLink = undefined;
    }

    linkOnSelect(link: TabLinkComponent): void {
        this.clearActive();
        this.activeTabLink = link;
        link.doAfterSelect();
    }

    public getActiveForm(): any {
        return this.activeForm;
    }

    private doActivate(item: NavItem): void {

        let formBodyType: Function = NewPlanStartXeroFormBodyComponent;
        this.activeId = item.id;
        this.canContinue = true;
        if (item.id === 'company') {
            formBodyType = ChooseCompanyFormBodyComponent;
            this.activeForm = this.companyForm;
        }
        if (item.id === 'settings') {
            formBodyType = NewPlanStartXeroFormBodyComponent;
            this.activeForm = this.startXeroForm;
        }
        else if (item.id === 'profitloss-accounts') {
            formBodyType = Xi2ProfitLossFormBodyComponent;

            if (!this.xi2Form.isMatchingsLoaded)
                this.canContinue = false;
            this.activeForm = this.xi2Form;
            this.xi2Form.reload();
        }
        else if (item.id === 'balancesheet-accounts') {
            formBodyType = Xi3BalanceSheetFormBodyComponent;
            if (!this.balanceForm.isMatchingsLoaded)
                this.canContinue = false;
            this.activeForm = this.balanceForm;
            this.balanceForm.reload();
        }
        else if (item.id === 'component-associations') {
            formBodyType = Xi4DataAssociationsFormBodyComponent;
            this.activeForm = this.xi4Form;
        }


        if (formBodyType === Xi3BalanceSheetFormBodyComponent || formBodyType === Xi4DataAssociationsFormBodyComponent) {

            if ((this.isConnectToExistingModeEnabled || this.isConnectToXero) && formBodyType === Xi3BalanceSheetFormBodyComponent)
                this.nextText = this.nextTextLocalize;
            if ((this.isConnectToExistingModeEnabled || this.isConnectToXero) && formBodyType === Xi4DataAssociationsFormBodyComponent)
                this.nextText = this.connectPlanText;
            else if (!this.isConnectToXero)
                this.nextText = this.createPlanText;
        }
        else {
            //this.nextBtn.enable();
            this.nextText = this.nextTextLocalize;
        }
        
        if (item.id === 'component-associations') {

            this.activeForm.reload();
        }

        this.detectChanges();
    }

    onActivate(item: NavItem, callback?: Function): void {

        const oldActiveForm: NewPlanInnerForm = this.activeForm;
        
        if (oldActiveForm && !oldActiveForm.isSaveClosePaneHidden()) return;

        if (oldActiveForm)
            oldActiveForm.onGoOut(() => {

                this.doActivate(item);
                if (callback) callback();

            });

        else {
            this.doActivate(item);
            if (callback) callback();
        }
        
    }

    nextClick(): void {

        let companyId = localStorage.getItem('selectedCompanyId') ?? '';
        this.xeroService.GetXeroLimitsByCompanyId(companyId).then((limits: XeroLimits) => {

            if (limits && limits.XDayLimitRemaining === 0) {
                let dialogCreationRes = this.dialogCreation.createErrorWarning(this.importLimitText,
                    this.canNotImportedText,
                    () => { this.redirectService.toAccount(); }, 260, 'Go to plans');

                dialogCreationRes.show();
            }
        });

        let current: NavItem;
        let next: NavItem | undefined;
        this.bottomPaneData.forEach((it: NavItem) => {

            if (next) return;
            if (current) next = it;
            if (it.id == this.activeId) current = it;
            
        });
        
        if (this.activeId == "balancesheet-accounts" && !this.isConnectToXero || this.activeId == "component-associations" && this.isConnectToXero) {
            this.activeId = "";
            this.activeForm.onCreatePlan();
        }
        else {

            if (!next) return;
            this.onActivate({ id: next.id, text: '' }, () => {
                this.el.nativeElement.querySelectorAll(':scope .bottom-div .itemCls.active').forEach(
                    (el: HTMLDivElement) => this.r.removeClass(el, 'disabled')
                )
            });

        }

    }

    public setXeroImportModeEnabled(enabled: boolean) {

        this.isXeroImportModeEnabled = enabled;
        this.chDet.detectChanges();

        if (this.isXeroImportModeEnabled) this.activateCompany();

        else this.activateSimplePlanCreate();


    }

    public getScrollPane(): ScrollComponent {
        return this.scroll;
    }

    public saveClosePanePlaceEl(): Element {
        return this.saveClosePanePlace ? this.saveClosePanePlace.nativeElement : null;
    }

    onCOASaved(event: boolean) {

    }

    public setConnectedToXero(): void {

        let isConnectLS = localStorage.getItem('isConnectToXero');
        this.isConnectToXero = (isConnectLS != null && isConnectLS !== '0');        
        this.bottomPaneData = this.isConnectToXero ? this.bottomPaneDataRelease : this.bottomPaneData;

    }

    public setConnectToExistingPlanMode(): void {        

        this.isConnectToExistingModeEnabled = true;
        // delete plan settings screen
        this.bottomPaneData.splice(1, 1);

    }

    public reloadAccountMatchings(): void {
        this.xi2Form.reloadPLAccounts();
        this.balanceForm.reloadBSAccounts();
    }

    public UpdateNewPlanData(company: XeroCompany): void {
        this.startXeroForm.UpdateNewPlanData(company);
        this.startXeroForm.setNewPlanData();
    }
}